import { useEffect, useRef, useState } from "preact/hooks";
import { api, type Draft, type DraftAttachment, type DraftFields, type Folders } from "../api";
import { attach, cleanEditorHtml, DOMAIN, formatRecipients, parseRecipients, signatureHtml } from "../compose";
import { formatSize, frameDocument } from "../mime";
import { sameWritingContent, writingHtml, type WritingProposal, type WritingSnapshot } from "../writing";
import { AiWriterLazy } from "./AiLazy";
import { Icon } from "./icons";
import { RecipientInput } from "./RecipientInput";

const SAVE_DELAY_MS = 800;
const OTHER = "__autre__";

const TITLES: Record<Draft["kind"], string> = { new: "Nouveau message", reply: "Réponse", forward: "Transfert" };

// Barre de mise en forme : commandes natives de l'éditeur du navigateur.
const TOOLS: { icon: string; label: string; run: () => void }[] = [
  { icon: "bold", label: "Gras", run: () => document.execCommand("bold") },
  { icon: "italic", label: "Italique", run: () => document.execCommand("italic") },
  { icon: "list", label: "Liste à puces", run: () => document.execCommand("insertUnorderedList") },
  { icon: "numbered", label: "Liste numérotée", run: () => document.execCommand("insertOrderedList") },
  { icon: "quote", label: "Citation", run: () => document.execCommand("formatBlock", false, "blockquote") },
  {
    icon: "link", label: "Lien", run: () => {
      const url = prompt("Adresse du lien (https://…) :")?.trim();
      if (!url) return;
      const href = /^(https?:|mailto:)/i.test(url) ? url : `https://${url}`;
      document.execCommand("createLink", false, href);
    },
  },
];

export function Compose({ draftId, fresh, folders, onClose, onSent, onSettings, notify }: {
  draftId: string;
  fresh: boolean;
  folders: Folders | null;
  onClose: () => void;
  onSent: (threadId: string) => void;
  onSettings: () => void;
  notify: (text: string) => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [otherLocal, setOtherLocal] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  const [showQuote, setShowQuote] = useState(false);
  const [status, setStatus] = useState<"" | "saving" | "saved">("");
  const [sending, setSending] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [writingUndo, setWritingUndo] = useState<{ before: WritingSnapshot; after: WritingSnapshot } | null>(null);
  const editor = useRef<HTMLDivElement>(null);
  const files = useRef<HTMLInputElement>(null);
  const pending = useRef<DraftFields>({});
  // Robin a-t-il écrit ou choisi quelque chose ? Sinon, un brouillon tout juste créé est effacé.
  const touched = useRef(false);
  const signatures = useRef<Map<string, string>>(new Map());
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Les signatures d'abord : une réponse dont l'expéditeur est choisi d'avance reçoit la sienne.
    const prefs = api.preferences().then((p) => {
      signatures.current = new Map(p.signatures.map((x) => [x.address, x.text]));
      // Une adresse d'un autre domaine reprend la signature de son adresse de réception.
      for (const a of p.aliases ?? []) if (!signatures.current.has(a.alias) && signatures.current.has(a.address)) signatures.current.set(a.alias, signatures.current.get(a.address)!);
    }).catch(() => {});
    Promise.all([api.draft(draftId), prefs]).then(([d]) => {
      setDraft(d);
      const known = folders?.addresses.some((a) => a.address === d.from) || folders?.aliases?.some((a) => a.alias === d.from);
      setFrom(d.from ? (known ? d.from : OTHER) : "");
      setOtherLocal(d.from && !known ? d.from.replace(`@${DOMAIN}`, "") : "");
      setTo(formatRecipients(d.to));
      setCc(formatRecipients(d.cc));
      setBcc(formatRecipients(d.bcc));
      setShowCc(d.cc.length + d.bcc.length > 0);
      setSubject(d.subject);
      setAttachments(d.attachments);
      setShowQuote(d.kind === "forward");
    }).catch((e) => setError((e as Error).message));
  }, [draftId]);

  // L'éditeur n'est rempli qu'une fois : le réécrire à chaque frappe déplacerait le curseur.
  useEffect(() => {
    if (draft && editor.current && !editor.current.dataset.ready) {
      editor.current.innerHTML = draft.html;
      editor.current.dataset.ready = "1";
      if (fresh && draft.from && !draft.html) applySignature(draft.from, false);
      if (draft.kind !== "new" || draft.to.length) editor.current.focus();
    }
  }, [draft]);

  const flush = async () => {
    clearTimeout(timer.current);
    const fields = pending.current;
    pending.current = {};
    if (Object.keys(fields).length === 0) return;
    setStatus("saving");
    try {
      await api.saveDraft(draftId, fields);
      setStatus("saved");
    } catch (e) {
      pending.current = { ...fields, ...pending.current };
      setStatus("");
      throw e;
    }
  };

  const change = (fields: DraftFields, byRobin = true) => {
    if (byRobin) touched.current = true;
    pending.current = { ...pending.current, ...fields };
    setStatus("");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush().catch((e) => notify((e as Error).message)), SAVE_DELAY_MS);
  };

  // En quittant l'écran (retour du téléphone, changement de dossier) : enregistre ce qui reste,
  // ou efface le brouillon s'il vient d'être ouvert et que rien n'y a été écrit.
  useEffect(() => () => {
    if (fresh && !touched.current) {
      clearTimeout(timer.current);
      api.deleteDraft(draftId).catch(() => {});
    } else {
      flush().catch(() => {});
    }
  }, []);

  const editorChanged = (byRobin = true) => {
    const html = cleanEditorHtml(editor.current?.innerHTML ?? "");
    change({ html, text: editor.current?.innerText.trim() ?? "" }, byRobin);
  };

  // Met la signature de l'adresse choisie, à la place de celle d'une autre adresse.
  const applySignature = (address: string | null, byRobin = true) => {
    const el = editor.current;
    if (!el) return;
    const text = address ? signatures.current.get(address) : undefined;
    const current = el.querySelector("[data-signature]");
    if (current) {
      if (text) current.outerHTML = signatureHtml(text);
      else {
        // La ligne vide ajoutée avant la signature part avec elle.
        const gap = current.previousElementSibling;
        if (gap?.innerHTML === "<br>") gap.remove();
        current.remove();
      }
    } else if (text) {
      el.insertAdjacentHTML("beforeend", `<div><br></div>${signatureHtml(text)}`);
    }
    editorChanged(byRobin);
  };

  const fromValue = (choice: string, local: string) => (choice === OTHER ? (local.trim() ? `${local.trim().toLowerCase()}@${DOMAIN}` : null) : choice || null);

  // Lit le texte présent dans l'éditeur, même avant l'enregistrement automatique.
  // Le clone est rendu hors écran pour conserver les retours à la ligne d'innerText.
  const writingSnapshot = (): WritingSnapshot => {
    const clone = editor.current!.cloneNode(true) as HTMLDivElement;
    clone.querySelectorAll("[data-signature]").forEach((s) => s.remove());
    clone.removeAttribute("contenteditable");
    clone.removeAttribute("aria-label");
    clone.setAttribute("aria-hidden", "true");
    clone.style.cssText = "position:fixed;left:-100000px;top:0;pointer-events:none";
    document.body.append(clone);
    const text = clone.innerText.trim();
    clone.remove();
    return {
      kind: draft!.kind, subject, html: editor.current!.innerHTML, text,
      quoteText: draft!.quoteText, from: fromValue(from, otherLocal), to, cc,
      attachments: attachments.map((a) => a.filename),
    };
  };

  const applyWriting = (proposal: WritingProposal, snapshot: WritingSnapshot) => {
    if (sending || !sameWritingContent(snapshot, writingSnapshot())) {
      throw new Error("Le brouillon a changé depuis cette proposition. Relancez la demande pour utiliser le texte actuel.");
    }
    const el = editor.current!;
    const signature = el.querySelector("[data-signature]")?.outerHTML ?? "";
    el.innerHTML = writingHtml(proposal.text) + (signature ? "<div><br></div>" + signature : "");
    const after = { ...snapshot, subject: proposal.subject, html: el.innerHTML, text: proposal.text };
    setSubject(proposal.subject);
    setWritingUndo({ before: snapshot, after });
    change({ subject: proposal.subject, html: cleanEditorHtml(el.innerHTML), text: el.innerText.trim() });
  };

  const undoWriting = () => {
    if (!writingUndo) return;
    if (sending || !sameWritingContent(writingUndo.after, writingSnapshot())) {
      throw new Error("Le brouillon a été modifié. Le texte actuel est conservé.");
    }
    const before = writingUndo.before;
    editor.current!.innerHTML = before.html;
    setSubject(before.subject);
    change({ subject: before.subject, html: cleanEditorHtml(before.html), text: editor.current!.innerText.trim() });
    setWritingUndo(null);
  };

  const addFiles = async (list: FileList | null) => {
    if (!list) return;
    touched.current = true;
    for (const file of Array.from(list)) {
      setUploading(file.name);
      try {
        const added = await attach(draftId, file, file.name);
        setAttachments((a) => [...a, added]);
      } catch (e) {
        notify(`${file.name} : ${(e as Error).message}`);
      }
    }
    setUploading(null);
    if (files.current) files.current.value = "";
  };

  const removeAttachment = async (a: DraftAttachment) => {
    await api.deleteAttachment(draftId, a.id).catch((e) => notify((e as Error).message));
    setAttachments((list) => list.filter((x) => x.id !== a.id));
  };

  const send = async () => {
    setSending(true);
    try {
      editorChanged();
      await flush();
      const sent = await api.sendDraft(draftId);
      notify("E-mail envoyé");
      onSent(sent.threadId);
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const discard = async () => {
    if (!confirm("Supprimer ce brouillon ?")) return;
    clearTimeout(timer.current);
    pending.current = {};
    await api.deleteDraft(draftId).catch(() => {});
    notify("Brouillon supprimé");
    onClose();
  };

  // Le collage garde la mise en forme simple, jamais les styles ni les scripts d'une page.
  const paste = (e: ClipboardEvent) => {
    const html = e.clipboardData?.getData("text/html");
    if (!html) return;
    e.preventDefault();
    document.execCommand("insertHTML", false, cleanEditorHtml(html));
  };

  if (error) return <section class="reader"><div class="placeholder">{error}</div></section>;
  if (!draft) return <section class="reader"><div class="placeholder">Chargement…</div></section>;

  const addresses = folders?.addresses.map((a) => a.address) ?? [];
  return (
    <section class="reader compose">
      <div class="reader-head">
        <button class="icon-btn" aria-label="Fermer" title="Fermer (le brouillon est gardé)" onClick={() => { if (touched.current) editorChanged(); onClose(); }}><Icon name="back" /></button>
        <strong class="compose-title">{TITLES[draft.kind]}</strong>
        <span class="save-status">{status === "saving" ? "Enregistrement…" : status === "saved" ? "Brouillon enregistré" : ""}</span>
        <span class="spacer" />
        <button class="icon-btn" title="Joindre un fichier" aria-label="Joindre un fichier" onClick={() => files.current?.click()}><Icon name="clip" /></button>
        <button class="icon-btn" title="Supprimer le brouillon" aria-label="Supprimer le brouillon" onClick={discard}><Icon name="trash" /></button>
        <button class="btn primary" onClick={send} disabled={sending || uploading !== null}>{sending ? "Envoi…" : "Envoyer"}</button>
        <input ref={files} type="file" multiple hidden onChange={(e) => addFiles((e.target as HTMLInputElement).files)} />
      </div>

      <div class="reader-body compose-body">
        <label class="field">
          <span>De</span>
          <select value={from} onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            setFrom(v);
            change({ from: fromValue(v, otherLocal) });
            applySignature(fromValue(v, otherLocal));
          }}>
            <option value="" disabled>Choisir l'adresse d'expédition…</option>
            {addresses.map((a) => <option value={a}>{a}</option>)}
            {folders?.aliases?.map((a) => <option value={a.alias}>{a.name ? `${a.name} <${a.alias}>` : a.alias}</option>)}
            <option value={OTHER}>Autre adresse…</option>
          </select>
        </label>
        {from === OTHER && (
          <label class="field">
            <span />
            <span class="local-part">
              <input value={otherLocal} placeholder="nouvelle-adresse" autocapitalize="none" spellcheck={false}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  setOtherLocal(v);
                  change({ from: fromValue(OTHER, v) });
                }}
                onBlur={() => applySignature(fromValue(OTHER, otherLocal))} />
              <span>@{DOMAIN}</span>
            </span>
          </label>
        )}
        <label class="field">
          <span>À</span>
          <RecipientInput label="Destinataires" value={to} onChange={(v) => { setTo(v); change({ to: parseRecipients(v) }); }} />
          {!showCc && <button class="link-btn" onClick={() => setShowCc(true)}>Cc / Cci</button>}
        </label>
        {showCc && (
          <>
            <label class="field">
              <span>Cc</span>
              <RecipientInput label="Copie" value={cc} onChange={(v) => { setCc(v); change({ cc: parseRecipients(v) }); }} />
            </label>
            <label class="field">
              <span>Cci</span>
              <RecipientInput label="Copie cachée" value={bcc} onChange={(v) => { setBcc(v); change({ bcc: parseRecipients(v) }); }} />
            </label>
          </>
        )}
        <label class="field">
          <span>Objet</span>
          <input value={subject} onInput={(e) => { const v = (e.target as HTMLInputElement).value; setSubject(v); change({ subject: v }); }} />
        </label>

        <div class="compose-ai-entry">
          <button type="button" class="chip" aria-expanded={showAi} aria-controls="writing-assistant"
            onClick={() => setShowAi(!showAi)}><Icon name="spark" size={16} />Aide IA</button>
        </div>
        {showAi && <AiWriterLazy getSnapshot={writingSnapshot} onApply={applyWriting}
          onUndo={writingUndo ? undoWriting : undefined} onSettings={onSettings} onClose={() => setShowAi(false)} />}
        <div class="toolbar" role="toolbar" aria-label="Mise en forme">
          {TOOLS.map((t) => (
            <button class="icon-btn" title={t.label} aria-label={t.label}
              // Garder la sélection de l'éditeur : le bouton ne doit pas prendre le focus.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { t.run(); editorChanged(); }}><Icon name={t.icon} size={18} /></button>
          ))}
        </div>
        <div ref={editor} class="editor" contentEditable role="textbox" aria-multiline="true" aria-label="Message"
          onInput={() => editorChanged()} onPaste={paste} />

        {(attachments.length > 0 || uploading) && (
          <div class="attachments">
            {attachments.map((a) => (
              <span class="attachment draft-attachment">
                <span class="name">{a.filename}</span>
                <span class="size">{formatSize(a.size)}</span>
                <button class="icon-btn" aria-label={`Retirer ${a.filename}`} onClick={() => removeAttachment(a)}>×</button>
              </span>
            ))}
            {uploading && <span class="attachment draft-attachment"><span class="name">{uploading}</span><span class="size">Ajout…</span></span>}
          </div>
        )}

        {draft.quoteHtml && (
          <div class="quoted">
            <button class="link-btn" onClick={() => setShowQuote(!showQuote)}>
              {showQuote ? "Masquer" : "Afficher"} {draft.kind === "forward" ? "le message transféré" : "le message cité"}
            </button>
            {showQuote && (
              <iframe class="mail-frame" title="Message d'origine" sandbox="allow-same-origin" referrerpolicy="no-referrer"
                srcdoc={frameDocument(draft.quoteHtml, false)}
                onLoad={(e) => {
                  const f = e.currentTarget as HTMLIFrameElement;
                  if (f.contentDocument?.documentElement) f.style.height = `${f.contentDocument.documentElement.scrollHeight}px`;
                }} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
